import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import trendDown from '../../assets/trendDown.svg';
import trendUp from '../../assets/trendUp.svg';
import { metricValueFormat } from '../../utils/metricValueFormat';
import GradientDefs, { TieredMetricColors, safeMetricColorIndex } from '../../components/metricMonthlySummaryCard/GradientDefs';
import { MetricCircularChart } from '../../components/metricCircularChart/MetricCircularChart';
import { getMeetsTarget, getExceedsTarget, isTieredScorecard } from '../../utils/metricAttainment';

interface MetricDetailsProps {
    metric: any;
    index: number;
}

const MetricDetails = (props: MetricDetailsProps) => {
    const getDirectionIcon = (id?: number) => {
        if (id === 1) return trendUp;
        if (id === 2) return trendDown;
        return null;
    };

    const getDirectionText = (id?: number) => {
        if (id === 1) return 'Ascending';
        if (id === 2) return 'Descending';
        return '';
    };

    const {
        target,
        metric_value: metricValue,
        metric_type_name: metricTypeName,
        metric_direction_id: metricDirectionId,
        max,
        min,
        percent_to_target: percentToTarget,
        metric_omitted: metricOmitted,
        scorecard_calculation_rule_id: scorecardCalculationRuleId,
        targets,
    } = props.metric || {};

    const isTiered = isTieredScorecard(scorecardCalculationRuleId);
    const meetsTarget = getMeetsTarget(targets);
    const exceedsTarget = getExceedsTarget(targets);
    const colorIndex = safeMetricColorIndex(props?.index ?? 0, metricOmitted);
    const tieredColorPair = TieredMetricColors[colorIndex] ?? TieredMetricColors[0];

    const formattedTarget = metricValueFormat(target, metricTypeName);
    const formattedValue = metricValueFormat(metricValue, metricTypeName);
    const formattedPercentToTarget = metricValueFormat(percentToTarget, metricTypeName);
    const formattedMin = metricValueFormat(min, metricTypeName);
    const formattedMax = metricValueFormat(max, metricTypeName);
    const formattedMeets = meetsTarget !== null ? metricValueFormat(meetsTarget, metricTypeName) : '--';
    const formattedExceeds = exceedsTarget !== null ? metricValueFormat(exceedsTarget, metricTypeName) : '--';

    return (
        <Card className="metric-card mt-1 p-2">
            <GradientDefs />
            <div className="row">
                <div className="col-3">
                    <MetricCircularChart metric={props?.metric} index={props?.index} />
                </div>
                <div className="col-8 mt-4">
                    {metricDirectionId !== undefined && (
                        <div className="direction-inline mb-3">
                            <img
                                src={getDirectionIcon(metricDirectionId) || ''}
                                alt="trend"
                                className="trend-icon"
                            />
                            <span className="direction-text" style={{ color: '#1A5AA5' }}>
                                {' '}
                                {getDirectionText(metricDirectionId)}{' '}
                                {metricDirectionId === 1 ? ' (min ≠ 0)' : ''}
                            </span>
                        </div>
                    )}
                    {/* Formula */}
                    <div className="metric-formula">
                        {!metricOmitted ? (
                            <span>
                                {metricDirectionId === 1 ? (
                                    <>
                                        (<strong>MIN</strong> ({formattedValue}, {formattedMax})
                                        {min > 0 ? ` - ${formattedMin}` : ''}) / ({formattedTarget}
                                        {''}
                                        {min > 0 ? ` - ${formattedMin}` : ''}) ={' '}
                                        <strong style={{ color: '#1A5AA5' }}>
                                            {formattedPercentToTarget}
                                        </strong>
                                    </>
                                ) : (
                                    <>
                                        ({formattedMin} - <strong>MAX</strong>({formattedValue}
                                        {max > 0 ? `, - ${formattedMax}` : ''})) / ({formattedMin} -{' '}
                                        {formattedTarget}) ={' '}
                                        <strong style={{ color: '#1A5AA5' }}>
                                            {formattedPercentToTarget}
                                        </strong>
                                    </>
                                )}
                            </span>
                        ) : (
                            <span>No Calculation Available</span>
                        )}
                    </div>

                    <Divider />

                    {/* Min / Meets / Exceeds / Max values */}
                    <div className="metric-values">
                        <div className="metric-min">
                            <span className="value">{formattedMin}</span>
                            <span className="label">Minimum Value</span>
                        </div>
                        {isTiered && (
                            <>
                                <div className="metric-min">
                                    <span className="value" style={{ color: tieredColorPair.dark }}>
                                        {formattedMeets}
                                    </span>
                                    <span className="label">Meets at</span>
                                </div>
                                <div className="metric-min">
                                    <span className="value" style={{ color: tieredColorPair.light }}>
                                        {formattedExceeds}
                                    </span>
                                    <span className="label">Exceeds at</span>
                                </div>
                            </>
                        )}
                        <div className="metric-max">
                            <span className="value">{formattedMax}</span>
                            <span className="label">Maximum Value</span>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default MetricDetails;
